import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import SubscriptionTableRow from './SubscriptionTableRow';
import SubscriptionTableHeader from './SubscriptionTableHeader';
import Loader from '../utils/Loader';
import { User } from "lucide-react";

const SubscriptionTable = ({ subscriptions, loading, sortConfig, onSort, getSubscriptionStatus }) => {
  const [openDropdownId, setOpenDropdownId] = useState(null);

  const handleDropdownToggle = (subscriptionId) => {
    setOpenDropdownId(openDropdownId === subscriptionId ? null : subscriptionId);
  };

  // Helper function to determine subscription status for display
  const displaySubscriptionStatus = (subscription) => {
    // If getSubscriptionStatus function is provided, use it
    if (getSubscriptionStatus) {
      return getSubscriptionStatus(subscription);
    }
    
    // Fallback to checking marked_as_paid
    if (subscription.marked_as_paid === '1' || subscription.marked_as_paid === true) {
      return 'paid';
    } else if (subscription.marked_as_paid === '0' || subscription.marked_as_paid === false) {
      return 'unpaid';
    } else if (subscription.plan_name === 'Free Trial') {
      return 'trial';
    }
    
    // Check other possible status fields
    if (subscription.status) {
      return subscription.status.toLowerCase();
    }
    
    return 'unknown';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader />
      </div>
    );
  }

  if (subscriptions.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="py-12 text-center"
      >
        <div className="mx-auto mb-6 w-24 h-24 text-gray-300">
          <User className="w-full h-full" />
        </div>
        <h3 className="mb-2 text-lg font-medium text-gray-900">No subscriptions found</h3>
        <p className="text-gray-500">Try adjusting your search or filter criteria.</p>
      </motion.div>
    );
  }

  const columns = [
    { key: 'user', label: 'Customer', sortable: true },
    { key: 'plan_name', label: 'Plan', sortable: true },
    { key: 'package_name', label: 'Package', sortable: true },
    { key: 'amount', label: 'Amount', sortable: true },
    { key: 'status', label: 'Status', sortable: true },
    { key: 'created_at', label: 'Created', sortable: true },
    { key: 'actions', label: 'Actions', sortable: false }
  ];

  return (
    <div className="w-full">
      {/* Mobile Responsive Notice */}
      <div className="block sm:hidden mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-700 text-center">
          Scroll horizontally to view all columns
        </p>
      </div>

      {/* Table Container with Horizontal Scroll */}
      <div className="table-container overflow-x-auto w-full bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="min-w-[1100px]">
          {/* Table Header */}
          <div className="bg-gray-50 border-b border-gray-200">
            <div className="grid grid-cols-[minmax(280px,2.5fr)_minmax(120px,1.5fr)_minmax(120px,1.5fr)_minmax(100px,1.2fr)_minmax(110px,1.3fr)_minmax(120px,1.5fr)_minmax(60px,0.7fr)] gap-3 px-4 py-3 lg:px-6 lg:py-4">
              {columns.map((column) => (
                <SubscriptionTableHeader
                  key={column.key}
                  column={column}
                  sortConfig={sortConfig}
                  onSort={onSort}
                />
              ))}
            </div>
          </div>

          {/* Table Body */}
          <div className="divide-y divide-gray-100">
            <AnimatePresence>
              {subscriptions.map((subscription, index) => {
                // Get status for this subscription
                const status = displaySubscriptionStatus(subscription);
                
                return (
                  <motion.div
                    key={subscription.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <SubscriptionTableRow
                      subscription={subscription}
                      status={status} // Pass status as prop
                      columns={columns}
                      isOpen={openDropdownId === subscription.id}
                      onToggle={() => handleDropdownToggle(subscription.id)}
                      getSubscriptionStatus={getSubscriptionStatus}
                    />
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>

          {/* Table Footer */}
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 lg:px-6 lg:py-4">
            <div className="flex flex-col gap-2 justify-between items-start text-sm text-gray-600 sm:flex-row sm:items-center">
              <span>
                Showing {subscriptions.length} subscription{subscriptions.length !== 1 ? 's' : ''}
              </span>
              <div className="flex gap-2 items-center">
                <span className="text-xs text-gray-500">
                  Click column headers to sort
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Scrollbar Styling */}
      <style jsx>{`
        .table-container {
          scrollbar-width: thin;
          scrollbar-color: #cbd5e1 #f1f5f9;
        }
        
        .table-container::-webkit-scrollbar {
          height: 8px;
        }
        
        .table-container::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 4px;
        }
        
        .table-container::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 4px;
        }
        
        .table-container::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>
    </div>
  );
};

export default SubscriptionTable;